import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Alert,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, CircularProgress, Divider, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';

// ─── Colores por estado de celda ──────────────────────────────────────────
const chipTurno = (t) => {
  const colors = ['primary', 'secondary', 'warning'];
  return colors[(t - 1) % colors.length];
};

const chipFaltante = (f) => (f === 0 ? 'success' : f === 1 ? 'warning' : 'error');

// ─── Tabla por día ─────────────────────────────────────────────────────────
const TablaDia = ({ maquinas }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.06)' }}>
          <TableCell sx={{ fontWeight: 700, minWidth: 110 }}>Máquina</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Regla</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700, width: 60 }}>Turno</TableCell>
          <TableCell sx={{ fontWeight: 700, width: 110 }}>Horario</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700, width: 60 }}>Grupo</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Operarios asignados</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700, width: 80 }}>Estado</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {maquinas.map((m) =>
          m.turnos.map((t, ti) => (
            <TableRow key={`${m.dispositivoId}-${t.turno}`} hover>
              {ti === 0 && (
                <TableCell rowSpan={m.turnos.length} sx={{ fontWeight: 600, verticalAlign: 'top', pt: 1.5 }}>
                  {m.dispositivoId}
                  <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                    {m.cargo}
                  </Typography>
                </TableCell>
              )}
              {ti === 0 && (
                <TableCell rowSpan={m.turnos.length} sx={{ verticalAlign: 'top', pt: 1.5, fontSize: 12, color: '#888', maxWidth: 140 }}>
                  {m.regla}
                </TableCell>
              )}
              <TableCell align="center">
                <Chip label={`T${t.turno}`} size="small" color={chipTurno(t.turno)} />
              </TableCell>
              <TableCell sx={{ fontSize: 12 }}>
                {t.inicio} – {t.fin}
              </TableCell>
              <TableCell align="center">
                <Chip label={t.grupoAsignado} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                {t.operarios.length === 0 ? (
                  <Typography variant="caption" sx={{ color: 'error.main' }}>Sin operarios disponibles</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                    {t.operarios.map((op) => (
                      <Tooltip key={op._id} title={op.grupoRotacion}>
                        <Chip label={op.nombre} size="small" variant="outlined" />
                      </Tooltip>
                    ))}
                  </Box>
                )}
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={t.faltantes === 0 ? 'OK' : `-${t.faltantes}`}
                  size="small"
                  color={chipFaltante(t.faltantes)}
                />
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

// ══════════════════════════════════════════════════════════════════════════
//  Componente principal
// ══════════════════════════════════════════════════════════════════════════
const LS_INICIO = 'agenteTurnos_fechaInicio';
const LS_FIN    = 'agenteTurnos_fechaFin';

const AgenteTurnos = () => {
  const [fechaInicio, setFechaInicio] = useState(() => localStorage.getItem(LS_INICIO) || '');
  const [fechaFin,    setFechaFin]    = useState(() => localStorage.getItem(LS_FIN)    || '');
  const [generando,   setGenerando]   = useState(false);
  const [guardando,   setGuardando]   = useState(false);
  const [plan,        setPlan]        = useState(null);   // { dias, resumen }
  const [error,       setError]       = useState('');
  const [mensaje,     setMensaje]     = useState(null);   // { tipo, texto }
  const [openConfirm, setOpenConfirm] = useState(false);

  const token = () => localStorage.getItem('token');

  // ── Generar plan ─────────────────────────────────────────────────────
  const handleGenerar = async (e) => {
    e.preventDefault();
    if (!fechaInicio || !fechaFin) {
      setError('Ambas fechas son obligatorias');
      return;
    }
    if (fechaFin < fechaInicio) {
      setError('La fecha de fin no puede ser anterior a la de inicio');
      return;
    }
    setError('');
    setMensaje(null);
    setPlan(null);
    setGenerando(true);
    try {
      const res = await fetch(
        `/api/agente-turnos/generar?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`,
        { credentials: 'include', headers: { 'x-access-token': token() } }
      );
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Error al generar el plan');
      } else {
        setPlan(data);
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setGenerando(false);
    }
  };

  // ── Confirmar (guarda en BD) ──────────────────────────────────────────
  const handleConfirmar = async (sobreescribir = false) => {
    setOpenConfirm(false);
    setGuardando(true);
    setMensaje(null);
    try {
      const res = await fetch('/api/agente-turnos/confirmar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-access-token': token() },
        credentials: 'include',
        body: JSON.stringify({
          fechaInicio,
          fechaFin,
          sobreescribir,
        }),
      });
      const data = await res.json();
      setMensaje({
        tipo:  res.ok ? 'success' : 'error',
        texto: data.message || (res.ok ? 'Guardado correctamente' : 'Error al guardar'),
      });
    } catch (err) {
      setMensaje({ tipo: 'error', texto: 'Error de conexión' });
    } finally {
      setGuardando(false);
    }
  };

  // ── Botón "Guardar plan": comprueba solapamiento primero ─────────────
  const checkAndGuardar = () => {
    if (plan?.resumen?.asignacionesExistentes > 0) {
      setOpenConfirm(true);
    } else {
      handleConfirmar(false);
    }
  };

  const { resumen } = plan || {};

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, px: 2 }}>
      {/* Cabecera */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <SmartToyIcon color="primary" fontSize="large" />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Agente IA — Programación de Turnos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Genera el plan de asignaciones basado en grupos de rotación, reglas y ausencias
          </Typography>
        </Box>
      </Box>

      {/* Formulario de período */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box component="form" onSubmit={handleGenerar} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextField
            label="Fecha de inicio"
            type="date"
            value={fechaInicio}
            onChange={e => { setFechaInicio(e.target.value); localStorage.setItem(LS_INICIO, e.target.value); setError(''); setPlan(null); }}
            InputLabelProps={{ shrink: true }}
            required
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="Fecha de fin"
            type="date"
            value={fechaFin}
            onChange={e => { setFechaFin(e.target.value); localStorage.setItem(LS_FIN, e.target.value); setError(''); setPlan(null); }}
            InputLabelProps={{ shrink: true }}
            required
            sx={{ minWidth: 180 }}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={generando}
            startIcon={generando ? <CircularProgress size={18} color="inherit" /> : <SmartToyIcon />}
          >
            {generando ? 'Generando…' : 'Generar plan'}
          </Button>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>

      {/* Resumen del plan */}
      {plan && resumen && (
        <>
          <Paper sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Resumen del plan generado
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <Chip label={`${resumen.diasGenerados} días`} color="primary" icon={<CheckCircleIcon />} />
              <Chip label={`${resumen.totalCeldas} celdas totales`} variant="outlined" />
              <Chip label={`${resumen.celdaCompleta} completas`} color="success" />
              {resumen.celdaIncompleta > 0 && (
                <Chip label={`${resumen.celdaIncompleta} incompletas`} color="warning" icon={<WarningAmberIcon />} />
              )}
              {resumen.asignacionesExistentes > 0 && (
                <Chip
                  label={`⚠ ${resumen.asignacionesExistentes} asignaciones existentes en este período`}
                  color="error"
                  variant="outlined"
                />
              )}
            </Box>

            {resumen.advertencias?.length > 0 && (
              <>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'warning.main' }}>
                  Advertencias ({resumen.advertencias.length}):
                </Typography>
                <Box sx={{ maxHeight: 140, overflowY: 'auto', pr: 1 }}>
                  {resumen.advertencias.map((adv, i) => (
                    <Typography key={i} variant="caption" display="block" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                      • {adv}
                    </Typography>
                  ))}
                </Box>
              </>
            )}

            <Box sx={{ mt: 2.5, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                size="large"
                disabled={guardando || !plan.dias?.length}
                onClick={checkAndGuardar}
                startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              >
                {guardando ? 'Guardando…' : 'Confirmar y guardar plan'}
              </Button>
              <Button variant="outlined" onClick={() => { setPlan(null); setMensaje(null); }}>
                Descartar
              </Button>
            </Box>

            {mensaje && (
              <Alert severity={mensaje.tipo} sx={{ mt: 2 }}>
                {mensaje.texto}
              </Alert>
            )}
          </Paper>

          {/* Preview por día (acordeón) */}
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Vista previa del plan — {plan.dias.length} días
          </Typography>

          {plan.dias.map((dia) => {
            const incompletas = dia.maquinas.flatMap(m => m.turnos).filter(t => t.faltantes > 0).length;
            return (
              <Accordion key={dia.fecha} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {dia.diaSemana} {dia.fecha}
                    </Typography>
                    <Chip label={`${dia.maquinas.length} máquinas`} size="small" variant="outlined" />
                    {incompletas > 0 && (
                      <Chip label={`${incompletas} celdas incompletas`} size="small" color="warning" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TablaDia maquinas={dia.maquinas} />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      )}

      {/* Diálogo confirmación sobreescritura */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>Asignaciones existentes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ya hay <strong>{resumen?.asignacionesExistentes}</strong> asignaciones guardadas en este período.
            ¿Deseas eliminarlas y reemplazarlas con el nuevo plan?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancelar</Button>
          <Button onClick={() => handleConfirmar(false)} variant="outlined">
            Agregar sin eliminar
          </Button>
          <Button onClick={() => handleConfirmar(true)} color="error" variant="contained">
            Sobreescribir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgenteTurnos;
